import React, { useState, SyntheticEvent, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { login } from '../../actions/loginAndRegisterActions';
import ErrorResponse from '../../components/ErrorResponse';
import Input from '../../components/Input';
import LinkWithIcon from '../../components/LinkWithIcon';
import PrimaryButton from '../../components/PrimaryButton';
import ViewWrapper, { FormWrapper } from '../../components/ViewWrapper';
import { paths } from '../../constants';
import { Store } from '../../reducers';
import { RequestType } from '../../reducers/requestReducer';
import InviteInfo from '../invite/InviteInfo';
import IFrameLogin from './IFrameLogin';
import LoginDefault from './LoginDefault';
import styles from './LoginView.module.scss';

// URLSearchParams does not allow strings with "+" in them
class UrlParams {
    private qs: string;
    private readonly params: { [key: string]: string };
    constructor(search: string) {
        this.qs = (search || location.search).substr(1);
        this.params = {};
        this.parseQuerystring();
    }
    parseQuerystring(): void {
        this.qs.split('&').reduce((a, b) => {
            const [key, val] = b.split('=');
            a[key] = decodeURIComponent(val);
            return a;
        }, this.params);
    }
    get(key: string): string {
        return this.params[key];
    }
}

export const useQuery = (): UrlParams => {
    const { search } = useLocation();
    return useMemo(() => new UrlParams(search), [search]);
};

const LoginView = (): JSX.Element => {
    const { email: userEmail, mode } = useSelector((state: Store) => state.user);
    const dispatch = useDispatch();
    const query = useQuery();
    const { invite } = useSelector((state: Store) => state.invite);
    const { loading: loadingLogin, error: errorLogin } = useSelector(
        (state: Store) => state.requests[RequestType.Login]
    );
    const { loading: loadingGetConsent, error: errorGetConsent } = useSelector(
        (state: Store) => state.requests[RequestType.GetUserConsentForClient]
    );
    const { loading: loadingConsent, error: errorConsent } = useSelector(
        (state: Store) => state.requests[RequestType.RegisterConsent]
    );
    const loading = loadingLogin || loadingConsent || loadingGetConsent;
    const error = errorLogin || errorConsent || errorGetConsent;

    const [password, setPassword] = useState('');
    const [email, setEmail] = useState('');
    const [validateInput, setValidateInput] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (email.length > 0 && password.length > 0) {
            setValidateInput(false);
        }
    }, [email, password]);

    useEffect(() => {
        if (error) {
            setErrorMessage(`ErrorCodes.${error.error}`);
        }
    }, [error]);

    useEffect(() => {
        if (validateInput) {
            setErrorMessage('Login.EmailAndPasswordAreRequired');
        } else {
            setErrorMessage('');
        }
    }, [validateInput]);

    const onChange = (value: string, key: string): void => {
        if (key === 'email') {
            setEmail(value.trim());
        } else if (key === 'password') {
            setPassword(value.trim());
        }
    };

    useEffect((): void => {
        const emailFromQuery = query.get('username');
        if (emailFromQuery) setEmail(emailFromQuery);
    }, []);

    useEffect(() => {
        if (invite) {
            setEmail(invite.email);
        } else if (userEmail) {
            setEmail(userEmail);
        }
    }, [invite, userEmail]);

    const loginClick = (event: SyntheticEvent<HTMLFormElement> | SyntheticEvent<HTMLButtonElement>): void => {
        event.preventDefault();
        if (email.length > 0 && password.length > 0) {
            dispatch(login(email, password));
        } else {
            setValidateInput(true);
        }
    };

    if (query.get('iframe') === 'true') {
        return <IFrameLogin />;
    }

    return (
        <ViewWrapper>
            <FormWrapper onSubmit={loginClick} testAttr="login-form">
                {invite ? (
                    <InviteInfo invite={invite} userRegistered />
                ) : (
                    <LoginDefault
                        onChange={onChange}
                        email={email}
                        error={error}
                        validateInput={validateInput}
                        mode={mode}
                    />
                )}
                <Input
                    label="User.Password"
                    id="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    value={password}
                    invalid={!!error || (validateInput && password.length === 0)}
                    onChange={onChange}
                />
                <LinkWithIcon iconName="help_outline" link={paths.resetPassword} linkText="Login.ForgotPassword" />
                <div className={styles.resendCodeLink}>
                    <LinkWithIcon
                        iconName="info"
                        link={paths.resendConfirmationCode}
                        linkText="Login.ResendConfirmationCode"
                    />
                </div>
                {errorMessage && <ErrorResponse error={errorMessage} testAttr={'login-error'} />}
                <PrimaryButton
                    onClick={loginClick}
                    type="submit"
                    loading={loading}
                    title="Login.Login"
                    testAttr="login-button"
                />
            </FormWrapper>
        </ViewWrapper>
    );
};

export default LoginView;
